/**
 * The numbers behind the two age charts on the detail pages (#586 AC3 + AC4).
 *
 * One row per chart series: "Alle" first, then one per selected brand — so two
 * selected brands give three rows. Until now the tabs showed a shape and no
 * figures, and the KPI cards above the chart only ever described the total.
 *
 * Snitt and median come from the API computed over the real ages, while the
 * chart clamps its x-axis (25 år for cars, 75 år for buyers) to stay readable.
 * For a brand with a long tail the grid will therefore read higher than the
 * chart looks — that is the point, and the footnote says so.
 *
 * Not to be confused with Aldersprofil/AgeStatsGrid, which serves the
 * standalone Aldersprofil tab and is bound to that page's own series shape.
 */

import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'

/** Structural, so both AlderSeries and KjoperAlderSeries satisfy it. */
export interface AlderStatsRow {
  merke: string
  total: number
  snittalder?: number | null
  medianalder?: number | null
}

interface Props {
  series: AlderStatsRow[]
  /** What the age column measures, e.g. "Bilalder" or "Kjøperalder". */
  ageLabel: string
  footnote: string
}

const fmtCount = (n: number) => n.toLocaleString('nb-NO')
const fmtAge = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : i18n.t('units.aar', {
      ns: 'bruktbil',
      value: n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    })

export default function AlderStatsGrid({ series, ageLabel, footnote }: Props) {
  const { t } = useTranslation('bruktbil')
  if (series.length === 0) return null

  return (
    <div className="card p-4 overflow-x-auto" data-testid="alder-stats-grid">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('alderStats.title')}</h3>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
          <tr>
            <th className="px-3 py-2">{t('common.merke')}</th>
            <th className="px-3 py-2 text-right">{t('common.antall')}</th>
            <th className="px-3 py-2 text-right">{t('alderStats.snitt', { label: ageLabel.toLowerCase() })}</th>
            <th className="px-3 py-2 text-right">{t('alderStats.median', { label: ageLabel.toLowerCase() })}</th>
          </tr>
        </thead>
        <tbody>
          {series.map((s, i) => (
            <tr
              key={s.merke}
              data-testid="alder-stats-row"
              className={
                // "Alle" is the line every brand is read against, so it carries
                // the same weight here as it does in the chart.
                s.merke === 'Alle'
                  ? 'bg-primary-50 dark:bg-primary-900/20 font-semibold'
                  : i % 2 === 0
                    ? 'bg-gray-50 dark:bg-gray-800/30'
                    : ''
              }
            >
              <td className="px-3 py-1.5">{s.merke === 'Alle' ? t('common.alle') : s.merke}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{fmtCount(s.total)}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{fmtAge(s.snittalder)}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{fmtAge(s.medianalder)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{footnote}</p>
    </div>
  )
}
